import React from 'react';
import { Box } from '@mui/material';
import { PhaserToReactListeners, RemoveListener } from 'Phaser/EventBus2';
import { clockStepOf, timingSummaryOf } from '../fingeringTiming';
import { aidPanelSx } from './testingAidStyles';

/** Milliseconds to three significant digits: 0.0612 reads 0.061, 1.234 reads 1.23. */
const formatted = (milliseconds: number): string =>
  milliseconds === 0 ? '0' : `${Number(milliseconds.toPrecision(3))}`;

/**
 * Testing aid for live finger numbers: the time the runtime has spent fingering
 * each phrase since the lesson opened, both hands together, a phrase fingered
 * again after a change of transposition counted again. A thin overlay in the
 * top left corner of the score (the right one holds the zoom control), so it
 * can be read while the lesson plays; it lets taps through.
 */
const FingeringTimingReadout = () => {
  const [samples, setSamples] = React.useState<number[]>([]);
  // Measured once: it reads the clock until it steps, which takes real time.
  const [clockStep] = React.useState(() => clockStepOf(() => performance.now()));

  React.useEffect(() => {
    const keepSample = ({ milliseconds }: { milliseconds: number }) =>
      setSamples((taken) => [...taken, milliseconds]);
    // A phrase fingered before this readout was listening is counted too.
    PhaserToReactListeners.SightReadingPhraseFingered(keepSample, undefined, false, true);
    return () => RemoveListener('SightReadingPhraseFingered', keepSample);
  }, []);

  const summary = timingSummaryOf(samples);

  return (
    <Box
      data-fingering-timing-readout
      role="status"
      aria-label="Fingering time per phrase"
      sx={{
        ...aidPanelSx,
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 2,
        borderRadius: '0 0 8px 0',
        padding: '2px 6px',
        fontFamily: 'ui-monospace, Menlo, monospace',
        fontSize: '10px',
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
      }}
    >
      {summary === null ? (
        <Box>fingering: no phrase timed yet</Box>
      ) : (
        <>
          <Box>
            fingering n={summary.count} last {formatted(summary.last)} mean{' '}
            {formatted(summary.mean)} ms
          </Box>
          <Box>
            median {formatted(summary.median)} p95 {formatted(summary.p95)} p99{' '}
            {formatted(summary.p99)} step {clockStep === null ? '?' : formatted(clockStep)}
          </Box>
        </>
      )}
    </Box>
  );
};

export default FingeringTimingReadout;
